import React, { useState, useEffect, useId, useContext } from "react";
import ReactDOM from "react-dom";
import { Link } from "react-router-dom";
import { useHttpClient } from "shared/hooks/http-hook";
import { AuthContext } from "shared/context/auth-context";
import { getDeviceId } from "shared/util/auth/device-id";
import { getCurrentLanguage } from "shared/utility/lang";
import { trackSuiteEvent } from "shared/util/track";
import { measureOpenAIRegistrationCompleted } from "shared/util/openai-ads";
import "./LeadCaptureModal.css";

const TITLES = {
  optimize_success: "Keep this account — it's free",
  time_based: "Want to keep this demo?",
  demo_exit_intent: "Want to keep this demo?",
  keep_account: "Keep this account",
};

const SUBTITLES = {
  optimize_success: (
    <>
      <p>You just optimized a route. Create a free account and keep exploring.</p>
      <p>The live demo sandbox is replaced with a smaller sample set so the screens aren&apos;t empty.</p>
    </>
  ),
  time_based: (
    <>
      <p>Free account. No credit card.</p>
      <p>We&apos;ll load sample schools and routes — not the 500-person demo dump — so you can keep clicking around.</p>
    </>
  ),
  keep_account: (
    <>
      <p>Free account. No credit card. Sample data is loaded so the dashboard isn&apos;t empty; replace it when you&apos;re ready.</p>
    </>
  ),
};

SUBTITLES.demo_exit_intent = SUBTITLES.time_based;

// An upload workspace holds the operator's own roster: nothing is swapped for sample data.
const WORKSPACE_TITLE = "Keep this workspace";
const WORKSPACE_SUBTITLE = (
  <>
    <p>Free account. No credit card. Your passengers, parents, and services stay exactly as they are.</p>
  </>
);

const isPasswordStrong = (password) =>
  typeof password === "string" &&
  password.length >= 8 &&
  /[A-Z]/.test(password) &&
  /[a-z]/.test(password) &&
  /\d/.test(password);

/**
 * Demo → real free account. Replaces the old lead-email / "Get My Free Setup"
 * pitch. Calendly stays as a secondary path for people who want a human.
 */
const LeadCaptureModal = ({ show, source, onClose, onSuccess }) => {
  const { sendRequest } = useHttpClient();
  const auth = useContext(AuthContext);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const titleId = useId();
  const descId = useId();

  useEffect(() => {
    if (show) {
      setSubmitted(false);
      setError(null);
      setSubmitting(false);
    }
  }, [show]);

  useEffect(() => {
    if (!show) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [show, onClose]);

  if (!show) return null;

  const canSubmit =
    name.trim().length > 1 && email.trim().includes("@") && isPasswordStrong(password) && accepted && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setError(null);

    try {
      const responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/clients/convert-demo-account`,
        "POST",
        JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          initpassword: password,
          deviceId: getDeviceId(),
          language: getCurrentLanguage(),
        }),
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        },
      );

      trackSuiteEvent("Demo — Keep Account Submitted", "button_click", { source: source || "manual" });

      if (typeof window.gtag === "function") {
        window.gtag("event", "conversion", {
          send_to: "AW-18077551564/9kEgCIDY6bQcEMyXhqxD",
        });
      }
      await measureOpenAIRegistrationCompleted({
        email: email.trim(),
        externalId: responseData.clientId || responseData.id,
        name: name.trim(),
      });

      setSubmitted(true);
      await new Promise((r) => setTimeout(r, 700));
      const clientId = responseData.clientId || responseData.id;
      if (clientId && responseData.token && auth.login) {
        await auth.login(clientId, responseData.token);
      }
      if (window.toast) {
        window.toast.success(
          responseData.keptData ? "Your account is ready — everything you set up is kept." : "Your account is ready — sample data is loaded.",
        );
      }
      setTimeout(() => {
        onSuccess();
      }, 800);
    } catch (err) {
      if (err.code === 429) {
        setError("Too many attempts. Please try again in a little while.");
      } else {
        setError(err?.message || "Something went wrong. Please try again.");
      }
      setSubmitting(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const isWorkspace = auth?.company?.demoKind === "workspace";
  const title = isWorkspace ? WORKSPACE_TITLE : TITLES[source] || TITLES.keep_account;
  const subtitle = isWorkspace ? WORKSPACE_SUBTITLE : SUBTITLES[source] || SUBTITLES.keep_account;

  const content = (
    <div className="lead-capture-overlay" onClick={handleOverlayClick} role="presentation">
      <div
        className="lead-capture-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="lead-capture-header">
          <button type="button" className="lead-capture-close" onClick={onClose} aria-label="Close dialog">
            ×
          </button>
          <h3 id={titleId}>{title}</h3>
          <div className="lead-capture-subtitle" id={descId}>
            {subtitle}
          </div>
        </div>
        <div className="lead-capture-body">
          {submitted ? (
            <div className="lead-capture-success">
              <div className="lead-capture-success-icon" aria-hidden="true" />
              <h4>You&apos;re in</h4>
              <p>Demo chrome is gone. Sample schools and routes are loaded — replace them with yours when you&apos;re ready.</p>
            </div>
          ) : (
            <>
              <form className="lead-capture-form" onSubmit={handleSubmit} noValidate>
                <label htmlFor="keep-account-name" className="lead-capture-label">
                  Your name
                </label>
                <input
                  id="keep-account-name"
                  type="text"
                  className="lead-capture-input"
                  placeholder="Jane Smith"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="name"
                  autoFocus
                  required
                />

                <label htmlFor="keep-account-email" className="lead-capture-label lead-capture-label--optional">
                  Work email
                </label>
                <input
                  id="keep-account-email"
                  type="email"
                  className="lead-capture-input"
                  placeholder="you@district.org"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                />

                <label htmlFor="keep-account-password" className="lead-capture-label lead-capture-label--optional">
                  Password
                </label>
                <input
                  id="keep-account-password"
                  type="password"
                  className="lead-capture-input"
                  placeholder="Create a password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
                <p className="lead-capture-hint">At least 8 characters, with upper, lower, and a number.</p>

                <label className="lead-capture-terms">
                  <input
                    type="checkbox"
                    checked={accepted}
                    onChange={(e) => setAccepted(e.target.checked)}
                  />
                  <span>
                    I agree to the <Link to="/terms">Terms of Service</Link> and{" "}
                    <Link to="/privacy">Privacy Policy</Link>.
                  </span>
                </label>

                <button type="submit" className="lead-capture-submit lead-capture-submit--full" disabled={!canSubmit}>
                  {submitting ? "Saving…" : "Keep this account — it's free"}
                </button>
              </form>
              {error && (
                <div className="lead-capture-error" role="alert">
                  {error}
                </div>
              )}
              <div className="lead-capture-divider">or</div>
              <a
                href="https://calendly.com/routebot/intro"
                target="_blank"
                rel="noopener noreferrer"
                className="lead-capture-book-link"
                onClick={() => trackSuiteEvent("Demo — Book a Demo (keep-account modal)", "button_click")}
              >
                Book a live demo call instead →
              </a>
            </>
          )}
        </div>
      </div>
    </div>
  );

  const portalRoot = document.getElementById("modal-hook");
  return portalRoot ? ReactDOM.createPortal(content, portalRoot) : content;
};

export default LeadCaptureModal;
